import React, { useState, useEffect } from 'react';
import { motion, useAnimationControls } from 'framer-motion';
import { TimerMode } from '../TimerEffect';

interface ParticlesEffectProps {
  progress: number;
  size: number;
  color: string;
  secondaryColor: string;
  opacity: number;
  speed: number;
  mode: TimerMode;
}

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  initialAngle: number;
  distance: number;
  color: string;
  opacity: number;
  speed: number;
}

const ParticlesEffect: React.FC<ParticlesEffectProps> = ({
  progress,
  size,
  color,
  secondaryColor,
  opacity,
  speed,
  mode
}) => {
  // 根据模式调整粒子行为
  const isCountdown = mode === 'countdown';
  
  // 粒子数量（根据进度和速度调整）
  const particleCount = Math.floor(30 * speed);
  
  // 生成随机粒子
  const generateParticles = (): Particle[] => {
    const particles: Particle[] = [];
    
    for (let i = 0; i < particleCount; i++) {
      const useMainColor = Math.random() > 0.3;
      particles.push({
        id: i,
        x: size / 2, // 所有粒子从中心开始
        y: size / 2,
        size: Math.random() * (size * 0.02) + (size * 0.01), // 随机粒子大小
        initialAngle: Math.random() * Math.PI * 2, // 随机方向
        distance: 0, // 初始距离为0
        color: useMainColor ? color : secondaryColor,
        opacity: Math.random() * 0.5 + 0.5,
        speed: (Math.random() * 0.5 + 0.5) * speed // 随机速度
      });
    }
    
    return particles;
  };
  
  // 粒子状态
  const [particles, setParticles] = useState<Particle[]>([]);
  
  // 初始化粒子
  useEffect(() => {
    setParticles(generateParticles());
  }, [size, color, secondaryColor, particleCount]); // 当关键参数变化时重新生成
  
  // 动画帧
  useEffect(() => {
    if (!particles.length) return;
    
    let animationFrameId: number;
    let lastTimestamp: number = 0;
    
    const updateParticles = (timestamp: number) => {
      if (!lastTimestamp) {
        lastTimestamp = timestamp;
        animationFrameId = requestAnimationFrame(updateParticles);
        return;
      }
      
      const deltaTime = (timestamp - lastTimestamp) / 16; // 基于60fps标准化
      lastTimestamp = timestamp;
      
      // 发射率 - 随着进度变化
      const emissionRate = isCountdown
        ? Math.max(0.05, (1 - progress) * 0.4) // 倒计时：越接近0发射率越高
        : Math.max(0.05, progress * 0.4); // 秒表：随时间增加发射率
      
      setParticles(prevParticles => {
        return prevParticles.map(particle => {
          // 只在粒子还在圈内时更新
          if (Math.random() < emissionRate || particle.distance > 0) {
            // 计算新位置
            const newDistance = particle.distance + (0.5 * particle.speed * deltaTime);
            const maxDistance = size / 2; // 最大飞行距离
            
            // 当粒子到达最大距离时重置
            if (newDistance > maxDistance) {
              return {
                ...particle,
                distance: 0,
                initialAngle: Math.random() * Math.PI * 2, // 随机新方向
                size: Math.random() * (size * 0.02) + (size * 0.01), // 随机新大小
                opacity: Math.random() * 0.5 + 0.5,
              };
            }
            
            return {
              ...particle,
              distance: newDistance,
            };
          }
          return particle;
        });
      });
      
      animationFrameId = requestAnimationFrame(updateParticles);
    };
    
    animationFrameId = requestAnimationFrame(updateParticles);
    
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [particles, progress, isCountdown, size]);
  
  // 动态调整整体不透明度
  const dynamicOpacity = Math.max(0.3, Math.min(1, opacity));
  
  // 计算活跃粒子比例 - 随着进度变化
  const activeRatio = isCountdown
    ? (1 - progress) * 0.7 + 0.3 // 倒计时：越接近0活跃度越高
    : progress * 0.7 + 0.3; // 秒表：随时间增加活跃度
  
  return (
    <div
      style={{
        width: size,
        height: size,
        position: 'relative',
        opacity: dynamicOpacity,
        pointerEvents: 'none',
      }}
    >
      {/* 中心光环 */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: size * 0.2,
          height: size * 0.2,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${color}AA 0%, ${color}00 100%)`,
          opacity: activeRatio * opacity,
        }}
      />
      
      {/* 渲染粒子 */}
      {particles.map(particle => {
        // 计算当前位置
        const angle = particle.initialAngle;
        const x = size / 2 + Math.cos(angle) * particle.distance;
        const y = size / 2 + Math.sin(angle) * particle.distance;
        
        // 根据距离计算不透明度（越远越淡）
        const distanceRatio = particle.distance / (size / 2);
        const particleOpacity = particle.opacity * (1 - distanceRatio) * activeRatio;
        
        return (
          <div
            key={particle.id}
            style={{
              position: 'absolute',
              width: particle.size,
              height: particle.size,
              backgroundColor: particle.color,
              borderRadius: '50%',
              top: y - particle.size / 2,
              left: x - particle.size / 2,
              opacity: particleOpacity,
              boxShadow: `0 0 ${particle.size}px ${particle.color}`,
            }}
          />
        );
      })}
    </div>
  );
};

export default ParticlesEffect; 